<template>
  <div
    ref="fs"
    class="forecast-scale"
  >
    <div class="forecast-label">
      <div>
        <span class="value">
          <!-- old statements may not have the potential annual award value, fall back to forecasted in this case -->
          <template v-if="isEos && distributions.length">{{ formatCurrency(distributions[distributions.length - 1].potential_annual_award ?? distributions[distributions.length - 1].forecasted_annual_award) }}</template>
          <template v-else>{{ formatCurrency(organization.totals.funding) }}</template>
        </span>
        <span class="label">Potential Award</span>
      </div>
    </div>
    <div
      id="slider"
      class="slider"
    />
    <div class="slider-axis">
      <div
        class="axis-labels"
        :class="detailsClass"
      >
        <div
          v-for="tier in tiers"
          :key="`tier-label-${tier.key}`"
          :class="`tier-label-${tier.key}`"
        >
          <span class="label">{{ tier.label }}</span>
          <span class="value">{{ formatCurrency(tier.value) }}</span>
          <div
            v-if="!client.hide_client_results"
            class="client-results"
          >
            <template v-if="client.performance.mode === 'revenue-margin'">
              <div>
                <span>{{ client.performance.current.revenue.label }}</span>
                <span>{{ formatCurrency(client.performance.current.revenue[tier.key]) }}</span>
              </div>
              <div>
                <span>{{ client.performance.current.margin.label }}</span>
                <span>{{ formatPercent(client.performance.current.margin[tier.key], 2) }}</span>
              </div>
            </template>
            <div>
              <span>{{ client.performance.current.profit.label }}</span>
              <span>{{ formatCurrency(client.performance.current.profit[tier.key]) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import UtilsMixin from "@/mixins/UtilsMixin";
import ColorsMixin from "@/mixins/ColorsMixin";
import VLcurrency from "@/composables/VLcurrency";
import VLpercent from '@/composables/VLpercent';

export default {
  name: "ForecastScale",

  components: {},

  mixins: [ UtilsMixin, ColorsMixin ],

  props: {
    tiers: {
      type: Object,
      required: true,
    },
    forecast: {
      type: Object,
      required: true,
    },
    distributions: {
      type: Array,
      required: false,
    },
    client: {
      type: Object,
      required: true,
    },
    isEos: {
      type: Boolean,
      default: false,
    },
    organization: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      triangleWidth: 40,
      min: 0,
      max: 4,
    }
  },

  computed: {
    performanceZone() {
      let forecast_value = this.organization.totals.funding
      if(this.isEos && this.distributions.length) {
        // old statements may not have the potential annual award value, fall back to forecasted in this case
        forecast_value = this.distributions[this.distributions.length - 1].potential_annual_award ?? this.distributions[this.distributions.length - 1].forecasted_annual_award
      }

      switch (true) {
        case (forecast_value < this.tiers.base.value):
          return 5
        case (forecast_value === this.tiers.base.value):
          return (10 * (forecast_value / this.tiers.base.value))
        case (forecast_value <= this.tiers.target.value && forecast_value > this.tiers.base.value ):
          return (10 + (40 * ((forecast_value - this.tiers.base.value) / (this.tiers.target.value - this.tiers.base.value))))
        case (forecast_value <= this.tiers.superior.value && forecast_value > this.tiers.target.value):
          return (50 + (40 * ((forecast_value - this.tiers.target.value) / (this.tiers.superior.value - this.tiers.target.value))))
        case (forecast_value > this.tiers.superior.value):
          return 95
        default:
          return 7
      }
    },
    forecastPosition() {
      return this.performanceZone
    },
    detailsClass() {
      if(this.client.hide_client_results || !this.client.performance.current) return ""
      if(this.client.performance.mode === "revenue-margin") return "details-profit-height"
      return "details-height"
    },
  },

  watch: {
    forecastPosition: function() {
      this.updateForecastPosition()
    },
  },

  mounted() {
    this.updateForecastPosition()
  },

  methods: {
    formatCurrency(value, decimals = 0, currency = 'USD') {
        return VLcurrency.formatCurrency(value, decimals, currency);
    },
    formatPercent(value, decimals = 0) {
        return VLpercent.formatPercent(value, decimals);
    },
    updateForecastPosition() {
      this.$el.style.setProperty('--forecast-position', `${this.forecastPosition}%`)
    },
  },
}
</script>

<style lang="scss" scoped>
@use "../../../../scss/_variables.scss";

.forecast-scale {
  --mask-top: 28.6%;
  --triangle-width: 40px;
  --triangle-base: calc(var(--triangle-width) / 2); // half the width of the caret

  --forecast-position: 0%;

  --triangle-center: var(--forecast-position);
  --triangle-left: calc(var(--triangle-center) - var(--triangle-base));
  --triangle-right: calc(var(--triangle-center) + var(--triangle-base));

  padding: 1.2rem 3.6rem;

  @media screen and (max-width: 600px) {
    display: flex;
    padding: 1.2rem 1.2rem;
  }

  .forecast-label {
    position: relative;

    width: 100%;

    @media screen and (min-width: 601px) {
      height: 4rem;
    }

    div {
      position: absolute;
      left: var(--forecast-position);

      transform: translateX(-50%);

      display: inline-flex;
      flex-direction: column;

      align-items: center;

      .label {
        margin-right: unset;
      }

      @media screen and (max-width: 600px) {
        left: 5%;
        transform: translateX(-20%);
        bottom: calc(var(--forecast-position) - 10%);
        flex-direction: column-reverse;
      }
    }
  }

  .slider {
    position: relative;

    width: 100%;
    height: 35px;

    margin-bottom: 1.334rem;

    clip-path: polygon(
            0 100%,
            0 var(--mask-top),
            var(--triangle-left) var(--mask-top),
            var(--triangle-center) 0,
            var(--triangle-right) var(--mask-top),
            100% var(--mask-top),
            100% 100%
    );

    
    background: linear-gradient(90deg, var(--brand-color-tint) 0%, var(--brand-color) 100%);
    
    @media screen and (max-width: 600px) {
      background: linear-gradient(0deg, var(--brand-color-tint) 0%, var(--brand-color) 100%);
      clip-path: polygon(
        var(--mask-top) 0%,
        var(--mask-top) calc(100% - var(--triangle-left)),
        20% calc(100% - var(--triangle-center)),
        var(--mask-top) calc(100% - var(--triangle-right)),
        var(--mask-top) 100%,
        55% 100%,
        55% 0%
      );
      height: initial;
    }

    @media screen and (max-width: 400px) {
      left: 20px;
    }
  }

  .slider-axis {
    display: flex;
    justify-content: center;

    width: 100%;

    .axis-labels {
      position: relative;
      width: 100%;
      @media screen and (max-width: 600px) {
        display: flex;
        flex-direction: column-reverse;
      }
      @media screen and (min-width: 601px) {
        height: 3.65rem;
      }

      .tier-label-base,
      .tier-label-target,
      .tier-label-superior
      {
        display: flex;
        flex-direction: column;
        align-items: center;

        @media screen and (min-width: 601px) {
          position: absolute;
          transform: translateX(-50%);
        }
        top: 0;
      }

      .tier-label-base {
        left: 10%;
      }

      .tier-label-target {
        left: 50%;
      }

      .tier-label-superior {
        left: 90%;
      }

      .client-results {
        margin-top: 0.25em;

        div {
          display: flex;
          justify-content: space-between;
          white-space: nowrap;

          &>span:first-child {
            text-transform: capitalize;
            margin-right: 1em;
          }

          &>span:last-child {
            color: variables.$color-light-grey-500;
          }
        }
      }
    }
    @media screen and (min-width: 601px) {
      .details-height {
        height: 6.625em;
      }

      .details-profit-height {
        height: 8.625em;
      }
    }
  }

  .label {
    font-weight: bold;
    font-family: variables.$font-primary;
    font-size: variables.$text-base;
    white-space: nowrap;

    text-transform: capitalize;

    @media screen and (max-width: 600px) {
      white-space: unset;
      text-align: center;
    }
  }

  .value {
    color: var(--highlight-color);
    font-family: variables.$font-secondary;
    font-weight: 800;
    font-size: variables.$text-lg;
  }
}
</style>